import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Copy, Loader2, Pencil, Plus, RefreshCw, Shield, ShieldCheck, Trash2, Users,
} from 'lucide-react'
import {
  ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th,
} from '@/components/erp/primitives'
import { SearchInput } from '@/shared/ui/filters'
import { EmptyState, ErrorStrip, Forbidden } from '@/shared/ui/states'
import { useDebounced } from '@/shared/hooks/useDebounced'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { fmtDateTime, fmtNumber } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import { getRole, listPermissionModules, listRoles, type RoleFilters } from './api'
import {
  CloneRoleDialog, CreateRoleDialog, DeleteRoleDialog, EditRoleDialog,
} from './RoleDialogs'
import { PermissionMatrix } from './PermissionMatrix'
import type { RoleResponse } from '@/shared/api/types'

/**
 * Roles & Permissions — dedicated page.
 *
 * Master/detail rather than a drawer: editing grants is a sustained task with a
 * preview step, not a glance, and it needs the width. The selected role lives in
 * `?role=` so a half-finished review is a shareable link.
 */
export function RolesPage() {
  const canRead = useCan('rbac:read')
  const canCreate = useCan('rbac:create')
  const canUpdate = useCan('rbac:update')
  const canDelete = useCan('rbac:delete')

  const [params, setParams] = useSearchParams()
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<RoleResponse | null>(null)
  const [cloning, setCloning] = useState<RoleResponse | null>(null)
  const [deleting, setDeleting] = useState<RoleResponse | null>(null)

  const filters = useMemo<RoleFilters>(() => ({
    q: params.get('q') ?? '',
    system: (params.get('system') ?? '') as RoleFilters['system'],
    assignable: (params.get('assignable') ?? '') as RoleFilters['assignable'],
    sort: params.get('sort') ?? '',
  }), [params])

  const [draftQ, setDraftQ] = useState(filters.q)
  const debouncedQ = useDebounced(draftQ, 300)
  useEffect(() => { setDraftQ(filters.q) }, [filters.q])

  const setParam = (k: string, v: string) => {
    const sp = new URLSearchParams(params)
    if (v) sp.set(k, v); else sp.delete(k)
    setParams(sp, { replace: true })
  }

  useEffect(() => {
    if (debouncedQ !== filters.q) setParam('q', debouncedQ)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ])

  const selectedId = params.get('role') ?? ''
  const filterKey = JSON.stringify(filters)

  const list = useLoad(() => listRoles(filters, 0, 200), [filterKey])
  const modules = useLoad(() => listPermissionModules(), [])
  // The list DTO carries permissions, but the detail call is the authoritative
  // one after a save — it also refreshes `version` for the next If-Match.
  const detail = useLoad(
    () => (selectedId ? getRole(selectedId) : Promise.resolve(null)),
    [selectedId],
  )

  const roles = list.data?.content ?? []
  const selected = detail.data ?? null

  // Auto-select the first role so the page never opens as a dead master list.
  useEffect(() => {
    if (!selectedId && roles.length > 0 && roles[0]) setParam('role', roles[0].id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles.length, selectedId])

  const refreshAll = () => { list.reload(); detail.reload() }

  if (!canRead) return <Forbidden what="roles and permissions" />

  const totalMembers = roles.reduce((n, r) => n + r.memberCount, 0)
  const customRoles = roles.filter((r) => !r.system).length
  const unassigned = roles.filter((r) => r.memberCount === 0).length

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Roles & Permissions"
        description="A role is a named group of permissions. Users hold permissions only through roles."
        right={
          <>
            <button className="erp-btn" onClick={refreshAll} aria-label="Refresh">
              <RefreshCw className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Refresh</span>
            </button>
            {canCreate && (
              <button className="erp-btn erp-btn-primary" onClick={() => setCreating(true)} aria-label="New role">
                <Plus className="h-3.5 w-3.5" /> <span className="axis-hide-sm">New role</span>
              </button>
            )}
          </>
        }
      />

      <KpiStrip>
        <MetricCard label="Roles" value={fmtNumber(roles.length)} sub={`${customRoles} custom`} accent />
        <MetricCard
          label="Permissions"
          value={fmtNumber((modules.data ?? []).reduce((n, m) => n + m.permissions.length, 0))}
          sub={`across ${(modules.data ?? []).length} modules`}
        />
        <MetricCard label="Assignments" value={fmtNumber(totalMembers)} sub="user-role links" />
        <MetricCard label="Unused roles" value={fmtNumber(unassigned)} sub="nobody holds them" />
      </KpiStrip>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] items-start">
        {/* Master */}
        <Panel bodyClassName="p-0" className="min-w-0">
          <div className="p-3 border-b border-border space-y-2">
            <SearchInput
              value={draftQ}
              onChange={setDraftQ}
              placeholder="Search roles…"
              className="!w-full"
            />
            <div className="flex items-center gap-1.5">
              <FilterChip
                label="All"
                active={filters.system === ''}
                onClick={() => setParam('system', '')}
              />
              <FilterChip
                label="Custom"
                active={filters.system === 'false'}
                onClick={() => setParam('system', 'false')}
              />
              <FilterChip
                label="System"
                active={filters.system === 'true'}
                onClick={() => setParam('system', 'true')}
              />
            </div>
          </div>

          <ErrorStrip message={list.error} />
          <div className="max-h-[32rem] overflow-y-auto">
            {list.loading && roles.length === 0 ? (
              <div className="px-4 py-8 text-center text-erp-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading roles…
              </div>
            ) : roles.length === 0 ? (
              <EmptyState
                icon={<Shield className="h-4 w-4" />}
                title="No roles match"
                hint="Only SUPER_ADMIN and VIEWER ship by default. Create the roles your team needs."
                action={canCreate
                  ? <button className="erp-btn erp-btn-primary" onClick={() => setCreating(true)}>
                      <Plus className="h-3.5 w-3.5" /> New role
                    </button>
                  : undefined}
              />
            ) : (
              <ul className="divide-y divide-border">
                {roles.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => setParam('role', r.id)}
                      className={cn(
                        'w-full text-left px-3 py-2.5 flex items-start gap-2.5 hover:bg-muted/40',
                        r.id === selectedId && 'bg-chocolate-soft/40 border-l-[3px] border-l-chocolate pl-[9px]',
                      )}
                    >
                      <span
                        className="mt-1 h-2.5 w-2.5 rounded-full shrink-0"
                        style={{ background: r.colour ?? 'var(--color-muted-foreground)' }}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5">
                          <span className="text-erp-sm font-medium truncate">{r.displayName}</span>
                          {r.system && (
                            <span className="shrink-0 text-erp-xs px-1 py-0.5 rounded border border-border text-muted-foreground">
                              system
                            </span>
                          )}
                          {!r.assignable && (
                            <span className="shrink-0 text-erp-xs px-1 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-700">
                              locked
                            </span>
                          )}
                        </span>
                        <span className="text-erp-xs font-mono text-muted-foreground block truncate">
                          {r.name}
                        </span>
                        <span className="text-erp-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                          <span className="inline-flex items-center gap-1">
                            <Users className="h-3 w-3" /> {r.memberCount}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <ShieldCheck className="h-3 w-3" /> {r.permissionCount}
                          </span>
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Panel>

        {/* Detail */}
        <div className="min-w-0 space-y-4">
          {!selected ? (
            <Panel>
              <EmptyState
                icon={<Shield className="h-4 w-4" />}
                title="Select a role"
                hint="Pick a role on the left to see and edit what it grants."
              />
            </Panel>
          ) : (
            <>
              <Panel bodyClassName="p-0">
                <div className="px-4 py-3 flex items-start gap-3 flex-wrap">
                  <span
                    className="mt-1 h-3 w-3 rounded-full shrink-0"
                    style={{ background: selected.colour ?? 'var(--color-muted-foreground)' }}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-erp-lg font-semibold tracking-tight truncate">
                        {selected.displayName}
                      </h3>
                      {selected.system && (
                        <span className="text-erp-xs px-1.5 py-0.5 rounded-md border border-border text-muted-foreground">
                          system role
                        </span>
                      )}
                    </div>
                    <div className="text-erp-xs font-mono text-muted-foreground">{selected.name}</div>
                    {selected.description && (
                      <p className="text-erp-sm text-muted-foreground mt-1">{selected.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {canUpdate && (
                      <button className="erp-btn" onClick={() => setEditing(selected)} aria-label="Edit">
                        <Pencil className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Edit</span>
                      </button>
                    )}
                    {canCreate && (
                      <button className="erp-btn" onClick={() => setCloning(selected)} aria-label="Clone">
                        <Copy className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Clone</span>
                      </button>
                    )}
                    {canDelete && !selected.system && (
                      <button className="erp-btn" onClick={() => setDeleting(selected)} aria-label="Delete">
                        <Trash2 className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Delete</span>
                      </button>
                    )}
                  </div>
                </div>
                <ErpTable
                  head={<><Th>Members</Th><Th>Permissions</Th><Th className="axis-hide-sm">Assignable</Th><Th className="axis-hide-sm">Created</Th></>}
                >
                  <tr>
                    <Td className="erp-metric">{fmtNumber(selected.memberCount)}</Td>
                    <Td className="erp-metric">{fmtNumber(selected.permissionCount)}</Td>
                    <Td className="axis-hide-sm">
                      {selected.assignable
                        ? 'Yes'
                        : <span className="text-amber-700">No — cannot be granted</span>}
                    </Td>
                    <Td className="axis-hide-sm erp-metric">{fmtDateTime(selected.createdAt)}</Td>
                  </tr>
                </ErpTable>
              </Panel>

              {modules.loading ? (
                <Panel>
                  <div className="py-6 text-center text-erp-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading catalogue…
                  </div>
                </Panel>
              ) : (
                <PermissionMatrix
                  role={selected}
                  modules={modules.data ?? []}
                  onSaved={refreshAll}
                />
              )}
            </>
          )}
        </div>
      </div>

      <CreateRoleDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(r) => { refreshAll(); setParam('role', r.id) }}
      />
      <EditRoleDialog role={editing} onClose={() => setEditing(null)} onSaved={refreshAll} />
      <CloneRoleDialog
        role={cloning}
        onClose={() => setCloning(null)}
        onCloned={(r) => { refreshAll(); setParam('role', r.id) }}
      />
      <DeleteRoleDialog
        role={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={() => { setParam('role', ''); refreshAll() }}
      />
    </div>
  )
}

function FilterChip({
  label, active, onClick,
}: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'h-7 px-2.5 rounded-md border text-erp-xs',
        active
          ? 'border-chocolate/50 bg-chocolate-soft/40 text-chocolate font-medium'
          : 'border-input text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
    </button>
  )
}
